import { create } from 'zustand';
import { formaStorageService } from '@/services/forma/storage-service';
import { WorkflowDataMap } from '@/types/workflow';
import {
	DEFAULT_WORKFLOW_STAGE,
	normalizePaletteState,
	PALETTE_WORKFLOW_SECTION,
	WorkflowStage,
} from '@/types/palette';
import { RoomData } from '@/components/SpacePlanningPanel/SpacePlanningTypes';
type ActiveObjectContext = {
	contextKey: string;
	selectedPath: string;
	functionSummary: string;
};
const STORAGE_KEY = 'workflow-state';

interface WorkflowStore {
	workflowData: WorkflowDataMap;
	loading: boolean;
	error: Error | null;
	isSaving: boolean;
	currentStage: WorkflowStage;
	objectLock: boolean,
	activeObjectContext: ActiveObjectContext | null;
	setWorkflowData: (section: number, data: any) => void;
	setCurrentStage: (stage: WorkflowStage) => Promise<void>;
	renameFunctionKeys: (renameMap: Record<string, string>, sections?: number[]) => void;
	renamePaletteAssignmentKeys: (renameMap: Record<string, string>) => void;
	refresh: () => Promise<void>;
	saveNow: () => Promise<void>;
	setObjectLock: (enabled: boolean) => void;
	setActiveObjectContext: (context: ActiveObjectContext | null) => void;
}

type StoredWorkflowState = {
	workflowData?: WorkflowDataMap;
	currentStage?: WorkflowStage;
};

const normalizeWorkflowData = (
	loaded: WorkflowDataMap,
	local: WorkflowDataMap
): WorkflowDataMap => {
	const merged: WorkflowDataMap = {
		...loaded,
		...local,
	};

	const paletteSectionData = merged[PALETTE_WORKFLOW_SECTION];
	const paletteSectionPayload =
		paletteSectionData && typeof paletteSectionData === 'object' ? paletteSectionData : {};

	merged[PALETTE_WORKFLOW_SECTION] = {
		...paletteSectionPayload,
		data: normalizePaletteState((paletteSectionPayload as { data?: unknown }).data),
	};

	return merged;
};

type SectionDataRecord = Record<string, { rooms?: RoomData[]; [key: string]: unknown }>;

const mergeRoomsById = (targetRooms: RoomData[] = [], sourceRooms: RoomData[] = []) => {
	const roomMap = new Map<string, RoomData>();
	targetRooms.forEach((room) => roomMap.set(String(room.spaceId), room));
	sourceRooms.forEach((room) => {
		const roomId = String(room.spaceId);
		if (!roomMap.has(roomId)) {
			roomMap.set(roomId, room);
		}
	});
	return Array.from(roomMap.values());
};

export const useWorkflowStore = create<WorkflowStore>((set, get) => {
	let saveTimer: NodeJS.Timeout | null = null;
	let pendingData: WorkflowDataMap | null = null;
	const debounceMs = 400;
	const loadTimeoutMs = 10000;

	const debouncedSave = async (dataToSave: WorkflowDataMap) => {
		try {
			set({ isSaving: true, error: null });
			const { currentStage } = get();
			await formaStorageService.setJSON(STORAGE_KEY, {
				workflowData: dataToSave,
				currentStage,
			});
		} catch (err) {
			console.error('Error saving workflow data:', err);
			set({
				error: err instanceof Error ? err : new Error('Failed to save workflow data'),
			});
		} finally {
			set({ isSaving: false });
		}
	};

	const scheduleSave = (data: WorkflowDataMap) => {
		pendingData = data;
		if (saveTimer) clearTimeout(saveTimer);
		saveTimer = setTimeout(() => {
			if (pendingData) {
				debouncedSave(pendingData);
				pendingData = null;
			}
		}, debounceMs);
	};

	return {
		workflowData: {},
		loading: true,
		error: null,
		isSaving: false,
		currentStage: DEFAULT_WORKFLOW_STAGE,
		objectLock: true,
		activeObjectContext: null,
		refresh: async () => {
			try {
				set({ loading: true, error: null });
				const data = await Promise.race([
					formaStorageService.getJSON<StoredWorkflowState | WorkflowDataMap>(STORAGE_KEY),
					new Promise<null>((_, reject) =>
						setTimeout(() => reject(new Error('Storage load timed out')), loadTimeoutMs)
					),
				]);

				const localData = get().workflowData;
				const pendingWorkflowData = pendingData ?? {};
				const loadedWorkflowData =
					data && typeof data === 'object' && 'workflowData' in data
						? (data.workflowData ?? {})
						: ((data as WorkflowDataMap) ?? {});
				const loadedCurrentStage =
					data && typeof data === 'object' && 'currentStage' in data
						? ((data as StoredWorkflowState).currentStage ?? DEFAULT_WORKFLOW_STAGE)
						: DEFAULT_WORKFLOW_STAGE;

				const mergedRaw: WorkflowDataMap = {
					...loadedWorkflowData,
					...localData,
					...pendingWorkflowData,
				};
				const normalizedData = normalizeWorkflowData(mergedRaw, {});

				set({
					workflowData: normalizedData,
					currentStage: loadedCurrentStage,
					loading: false,
				});
			} catch (err) {
				console.error('Error loading workflow data:', err);
				set({
					error: err instanceof Error ? err : new Error('Failed to load workflow data'),
					loading: false,
				});
			}
		},

		saveNow: async () => {
			if (saveTimer) clearTimeout(saveTimer);
			if (pendingData) {
				await debouncedSave(pendingData);
				pendingData = null;
			}
		},

		setWorkflowData: (section, data) => {
			set((state) => {
				const normalizedSectionData =
					section === PALETTE_WORKFLOW_SECTION
						? {
								...(data && typeof data === 'object' ? data : {}),
								data: normalizePaletteState(
									(data as { data?: unknown } | undefined)?.data
								),
							}
						: data;

				const updatedData: WorkflowDataMap = {
					...state.workflowData,
					[section]: normalizedSectionData,
				};
				scheduleSave(updatedData);
				return { workflowData: updatedData };
			});
		},
		setCurrentStage: async (stage) => {
			set({ currentStage: stage });

			try {
				set({ isSaving: true, error: null });
				const { workflowData } = get();
				await formaStorageService.setJSON(STORAGE_KEY, {
					workflowData,
					currentStage: stage,
				});
			} catch (err) {
				console.error('Error saving current stage:', err);
				set({
					error: err instanceof Error ? err : new Error('Failed to save current stage'),
				});
			} finally {
				set({ isSaving: false });
			}
		},

		renameFunctionKeys: (renameMap, sections = [4]) => {
			set((state) => {
				if (!renameMap || Object.keys(renameMap).length === 0) return state;
				const updatedData: WorkflowDataMap = { ...state.workflowData };
				let changed = false;

				sections.forEach((section) => {
					const sectionState = updatedData[section];
					const sectionData = sectionState?.data;
					if (!sectionState || !sectionData || typeof sectionData !== 'object') return;

					const nextSectionData = { ...(sectionData as SectionDataRecord) };

					Object.entries(renameMap).forEach(([fromKey, toKey]) => {
						if (!fromKey || !toKey || fromKey === toKey) return;
						const fromValue = nextSectionData[fromKey];
						if (!fromValue) return;

						const toValue = nextSectionData[toKey];
						if (!toValue) {
							nextSectionData[toKey] = fromValue;
						} else {
							nextSectionData[toKey] = {
								...fromValue,
								...toValue,
								rooms: mergeRoomsById(toValue.rooms, fromValue.rooms),
							};
						}

						delete nextSectionData[fromKey];
						changed = true;
					});

					updatedData[section] = {
						...sectionState,
						data: nextSectionData,
					};
				});

				if (!changed) return state;
				scheduleSave(updatedData);
				return { workflowData: updatedData };
			});
		},

		renamePaletteAssignmentKeys: (renameMap) => {
			set((state) => {
				if (!renameMap || Object.keys(renameMap).length === 0) return state;

				const paletteSection = state.workflowData[PALETTE_WORKFLOW_SECTION];
				const paletteSectionData = paletteSection?.data;
				if (!paletteSection || !paletteSectionData) return state;

				const paletteState = normalizePaletteState(paletteSectionData);
				const nextFunctionAssignments = { ...paletteState.functionAssignments };
				const nextAreaAssignments = { ...paletteState.typologyAssignments };
				let changed = false;

				Object.entries(renameMap).forEach(([fromKey, toKey]) => {
					if (!fromKey || !toKey || fromKey === toKey) return;

					if (fromKey in nextFunctionAssignments) {
						if (!(toKey in nextFunctionAssignments)) {
							nextFunctionAssignments[toKey] = nextFunctionAssignments[fromKey];
						}
						delete nextFunctionAssignments[fromKey];
						changed = true;
					}

					Object.keys(nextAreaAssignments).forEach((areaKey) => {
						if (!areaKey.startsWith(`${fromKey}::`)) return;
						const renamedAreaKey = areaKey.replace(`${fromKey}::`, `${toKey}::`);
						if (!(renamedAreaKey in nextAreaAssignments)) {
							nextAreaAssignments[renamedAreaKey] = nextAreaAssignments[areaKey];
						}
						delete nextAreaAssignments[areaKey];
						changed = true;
					});
				});

				if (!changed) return state;

				const updatedData: WorkflowDataMap = {
					...state.workflowData,
					[PALETTE_WORKFLOW_SECTION]: {
						...paletteSection,
						data: {
							...paletteState,
							functionAssignments: nextFunctionAssignments,
							typologyAssignments: nextAreaAssignments,
							updatedAt: new Date().toISOString(),
						},
					},
				};

				scheduleSave(updatedData);
				return { workflowData: updatedData };
			});
		},

		setObjectLock: (enabled) => {
			set({ objectLock: enabled });
		},

		setActiveObjectContext: (context) => {
			set({ activeObjectContext: context });
		},
	};
});
